import { createContext, type ReactNode, useContext } from "react";
import type {
	ApiBlockGridItemModel,
	ApiBlockGridModel,
} from "#/_generated/umbraco/delivery-api/models";
import { ElementTypes } from "./element-types";

const MediaContext = createContext<{ priorityId?: string; fraction: number }>({
	fraction: 1,
});
type LeadingProperties = {
	blockGrid?: ApiBlockGridModel;
	image?: unknown[];
	videoFromExternalLink?: string;
	videoFromMediaLibrary?: unknown[];
};
type Visibility = {
	hideBlock?: boolean;
	publishStartDate?: string;
	publishEndDate?: string;
	responsiveVisibility?: string[];
};

/** Follow only the leading layout branch; later media is not assumed above fold. */
export function findLeadingMediaBlock(
	items?: readonly ApiBlockGridItemModel[],
	now = Date.now(),
): string | undefined {
	const first = items?.find((item) => {
		const settings = item.settings?.properties as Visibility | undefined;
		return (
			!settings?.hideBlock &&
			!(Date.parse(settings?.publishStartDate ?? "") > now) &&
			!(Date.parse(settings?.publishEndDate ?? "") < now)
		);
	});
	if (!first) return undefined;
	const visibility = (first.settings?.properties as Visibility | undefined)
		?.responsiveVisibility;
	if (
		visibility?.length &&
		!["xs", "sm", "md", "lg", "xl"].every((size) => visibility.includes(size))
	)
		return undefined;
	const type = first.content.contentType;
	const props = first.content.properties as LeadingProperties | undefined;
	if (
		new Set<string>([
			ElementTypes.Image,
			ElementTypes.ImageAndText,
			ElementTypes.Hero,
		]).has(type) &&
		props?.image?.length &&
		!props.videoFromExternalLink &&
		!props.videoFromMediaLibrary?.length
	)
		return first.content.id;
	if (type === ElementTypes.Carousel) return first.content.id;
	if (type === ElementTypes.NestedLayout)
		return findLeadingMediaBlock(props?.blockGrid?.items, now);
	if (type.endsWith("UmbBlockGrid")) {
		const background = first.settings?.properties as
			| LeadingProperties
			| undefined;
		if (background?.image?.length) return first.content.id;
		return findLeadingMediaBlock(first.areas?.[0]?.items, now);
	}
	return undefined;
}

export function MediaComposition({
	items,
	enabled,
	children,
}: {
	items?: readonly ApiBlockGridItemModel[];
	enabled?: boolean;
	children: ReactNode;
}) {
	const parent = useContext(MediaContext);
	return (
		<MediaContext
			value={
				enabled
					? { priorityId: findLeadingMediaBlock(items), fraction: 1 }
					: parent
			}
		>
			{children}
		</MediaContext>
	);
}

export function BlockMediaScope({
	columnSpan = 12,
	children,
}: {
	columnSpan?: number;
	children: ReactNode;
}) {
	const parent = useContext(MediaContext);
	const span =
		Number.isInteger(columnSpan) && columnSpan >= 1 && columnSpan <= 12
			? columnSpan
			: 12;
	const fraction = (parent.fraction * span) / 12;
	return (
		<MediaContext value={{ ...parent, fraction }}>{children}</MediaContext>
	);
}

export function useCmsImageOptions(
	id: string,
	layout: "full" | "half" | "hero" = "full",
	priority?: boolean,
	sizes?: string,
) {
	const context = useContext(MediaContext);
	const desktop = context.fraction * 100;
	return {
		priority: priority ?? context.priorityId === id,
		sizes:
			sizes ??
			(layout === "half"
				? `(min-width: 1024px) ${desktop / 2}vw, (min-width: 768px) 50vw, 100vw`
				: layout === "hero"
					? `(min-width: 1280px) ${(desktop * 7) / 12}vw, (min-width: 1024px) ${(desktop * 2) / 3}vw, 100vw`
					: `(min-width: 1024px) ${desktop}vw, 100vw`),
	};
}
